import React, { useCallback, useEffect, useRef } from "react";
import { ISocket } from "./SocketProvider.types";
import { useAuth } from "../AuthProvider/AuthProvider";
import { SocketContext } from "./SocketProvider.context";
import { initializeSocket, isValidOutlet } from "./SocketProvider.utils";
import { WhtasAppMessageAlert } from "./components/WhtasAppMessageAlert";
import { useBillingCompanyContext } from "../../providers";
import "../../assets/sounds/order_notification.mp3";
import { config } from "../../config";
export const SocketProvider: React.FC<{ children: React.ReactNode }> = (
  props
) => {
  const [appSocket, setAppSocket] = React.useState<ISocket>(null);
  const { user } = useAuth();
  const { userUuid } = user;
  const company = useBillingCompanyContext();
  const currentOutletUuidRef = useRef<string | null>(null);


  // ----------------------------
  // 🔊 Notification Sound Player
  // ----------------------------
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio("/assets/sounds/order_notification.mp3");
    audio.addEventListener("error", () => {
      console.error("❌ Audio failed to load. Check file path.");
    });
    notificationAudioRef.current = audio;
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!notificationAudioRef.current) return;
    notificationAudioRef.current.currentTime = 0;
    console.log("<--- Playing sound --->");
    notificationAudioRef.current
      .play()
      .then(() => console.log("Sound played"))
      .catch((err) => console.error("Audio play blocked:", err));
  }, []);


  useEffect(() => {
    if (!userUuid || !config.integration.isSocketEnabled) return;
    try {
      const newSocket = initializeSocket(userUuid);
      newSocket.on("connect", () => {
        setAppSocket(newSocket);
        console.log("✅ Socket connected:", newSocket.connected, newSocket.id);

        const companyOutletUuid = company?.comapny?.company_outlet_uuid;

        if (isValidOutlet(companyOutletUuid) && !currentOutletUuidRef.current) {
          newSocket.emit("join_outlet", companyOutletUuid!);
          currentOutletUuidRef.current = companyOutletUuid!;
          console.log(`User ${userUuid} joined OUTLET_${companyOutletUuid}`);
        }
      });

      newSocket.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
        setAppSocket(null);
      });

      newSocket.on("disconnect", () => {
        console.log("❌ Socket disconnected");
        setAppSocket(null);
        currentOutletUuidRef.current = null;
      });

      return () => {
        newSocket.disconnect();
        currentOutletUuidRef.current = null;
      };
    } catch (error) {
      console.error("Failed to initialize socket:", error);
      setAppSocket(null);
    }
  }, [userUuid]);

  useEffect(() => {
    if (!appSocket || !appSocket.connected) return;
    const companyOutletUuid = company?.comapny?.company_outlet_uuid;
    if (!isValidOutlet(companyOutletUuid)) return;
    if (!currentOutletUuidRef.current) {
      appSocket.emit("join_outlet", companyOutletUuid!);
      currentOutletUuidRef.current = companyOutletUuid!;
      console.log(`User ${userUuid} joined OUTLET_${companyOutletUuid}`);
    }
    else if (currentOutletUuidRef.current !== companyOutletUuid) {
      appSocket.emit("switch_outlet", companyOutletUuid!);
      currentOutletUuidRef.current = companyOutletUuid!;
      console.log(`User ${userUuid} switched to OUTLET_${companyOutletUuid}`);
    }
  }, [appSocket, company?.comapny?.company_outlet_uuid, userUuid]);

  return (
    <SocketContext.Provider
      value={{ socket: appSocket, playNotificationSound }}
    >
      <WhtasAppMessageAlert />
      {props.children}
    </SocketContext.Provider>
  );
};
